import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SuccessMessage from '../../../components/info-messages/SuccessMessage';
import InputAmount from '../input-amount/InputAmount';
import PaymentMethods from '../payment-methods/PaymentMethods';
import { getFormActions } from '../../../utils/form-helpers';
import formConfig, { canChoosePaymentMethod } from '../../../payment/payment-form-config';
import { formatMoneyKr } from '../../../utils';
import { useProject } from '../../project-hooks/useProject';
import { NameAndCommentContainer } from '../name-and-comment/NameAndCommentContainer';
import useQueryString from '../../../components/hooks/useQueryString';
import isInt from 'validator/lib/isInt';
import useDebounce from '../../../components/hooks/debounce';
import I18nText from '../../i18n/I18nText';
import { I_WANT_TO_SUPPORT_TEXT_KEY, isUserUsingNorwegianLanguageOnProject } from '../../i18n/project-i18n-model';
import styles from './give-support.module.less';
import InternationalInputAmount from '../input-amount/InternationalInputAmount';

const { setFormField } = getFormActions(formConfig);

const GiveSupport = () => {
    const dispatch = useDispatch();
    const [{ name, amount, message, hideDefaultAmounts }] = useQueryString();
    const [giverName, setGiverName] = useState(name);
    const [comment, setComment] = useState(message);
    const [realAmount, setRealAmount] = useState(null);
    const [amountError, setAmountError] = useState(null);

    const project = useProject();
    const paymentForm = useSelector((state) => state.paymentForm);
    const paymentConfig = useSelector((state) => state.paymentConfig);
    const errors = useSelector((state) => state.projects.errors);
    const expandedNameAndComment = project?.profile?.expandedNameAndComment;
    const canChoosePaymentMethodFromState = canChoosePaymentMethod({
        amount: paymentForm.amount,
        giveMinAmount: project?.profile?.giveMinAmount || paymentConfig?.giveMinAmount,
        giveMaxAmount: paymentConfig?.giveMaxAmount,
        rewards: paymentForm.rewards,
    });
    const debouncedAmount = useDebounce(paymentForm?.amount, 1000);

    useEffect(() => {
        if (amount && isInt(amount)) {
            dispatch(setFormField('amount', amount));
        }
        return () => {
            dispatch(setFormField('processingPayment', false));
            dispatch(setFormField('amount', null));
        };
    }, [dispatch, amount]);

    useEffect(() => {
        if (
            !paymentForm?.amount ||
            paymentForm?.amount >= (project?.profile?.giveMinAmount || paymentConfig?.giveMinAmount)
        ) {
            setAmountError('');
            return;
        }
        if (debouncedAmount && debouncedAmount < (project?.profile?.giveMinAmount || paymentConfig?.giveMinAmount)) {
            setAmountError({
                amount: `Du må gi minst ${formatMoneyKr(
                    project?.profile?.giveMinAmount || paymentConfig?.giveMinAmount
                )} på denne spleisen`,
            });
        } else {
            setAmountError('');
        }
    }, [paymentForm?.amount, debouncedAmount, project?.profile?.giveMinAmount, paymentConfig?.giveMinAmount]);

    const setAmount = (amount) => {
        try {
            if (project?.matchingGift?.matching) {
                if (project.collected_amount / 2 < project.matchingGift.maxAmount) {
                    const maxDoubledAmount = project.matchingGift.maxAmount - project.collected_amount / 2;
                    setRealAmount(maxDoubledAmount > amount ? amount * 2 : amount + maxDoubledAmount);
                }
            }
            dispatch(setFormField('amount', amount));
        } catch (e) {}
    };

    return (
        <>
            <div className={styles.youHaveChosen}>
                <I18nText textKey={I_WANT_TO_SUPPORT_TEXT_KEY} />
            </div>
            <div>
                {isUserUsingNorwegianLanguageOnProject(project) ? (
                    <InputAmount
                        projectId={project.id}
                        projectProfile={project.profile}
                        errors={{ ...errors, ...amountError }}
                        onAmountChange={setAmount}
                        hideDefaultAmounts={hideDefaultAmounts}
                    />
                ) : (
                    <InternationalInputAmount
                        projectId={project.id}
                        projectProfile={project.profile}
                        errors={{ ...errors, ...amountError }}
                        onAmountChange={setAmount}
                        initialAmount={amount}
                    />
                )}
                {project?.matchingGift &&
                    project?.matchingGift.matching &&
                    project.collected_amount / 2 < project.matchingGift.maxAmount && (
                        <SuccessMessage className={styles.matchingGiftInfo} hideIcon={true}>
                            {realAmount ? (
                                <>
                                    {project.matchingGift?.orgName} dobler til {formatMoneyKr(realAmount)} 🙌
                                </>
                            ) : (
                                <>{project.matchingGift?.orgName} dobler beløpet du gir 🙌</>
                            )}
                        </SuccessMessage>
                    )}
                <NameAndCommentContainer
                    giverName={giverName}
                    setGiverName={(giverName) => setGiverName(giverName)}
                    comment={comment}
                    setComment={(c) => setComment(c)}
                    className={styles.commentContainer}
                    isOpen={expandedNameAndComment || giverName || comment}
                />
                <PaymentMethods
                    amount={Math.round(paymentForm.amount)}
                    disabled={!canChoosePaymentMethodFromState}
                    giverName={giverName}
                    comment={comment}
                />
            </div>
        </>
    );
};

export default GiveSupport;
